import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DndContext, PointerSensor, closestCenter, useDraggable, useDroppable, useSensor, useSensors, } from '@dnd-kit/core';
import { useId, useState } from 'react';
import { FieldMessage } from '../../atoms/FieldMessage/FieldMessage.js';
import { IconButton } from '../../atoms/IconButton/IconButton.js';
import { Select } from '../../atoms/Select/Select.js';
import * as Icon from '../../icons.js';
import { cn } from '../../lib/cn.js';
import { SILENT_DRAG_ACCESSIBILITY } from '../../lib/drag.js';
import { LiveRegion } from '../Feedback/Feedback.js';
import { useTranslate } from '../../lib/i18n.js';
import { assign, clear, leftHolding, unmatched } from './matching.js';
import { RichText } from '../../atoms/RichText/RichText.js';
const UNMATCHED = '__none__';
/**
 * Match each item on the left with the one it belongs with on the right (the
 * MATCHING question type).
 *
 * **A dropdown is the interaction, not the fallback.** Every left item carries
 * a select listing the right items still free plus whatever is already on it,
 * which is one control that works with a mouse, a finger and a keyboard, reads
 * correctly to a screen reader, and needs no gesture nobody taught the student.
 * It is also what the layout wants on a phone, where two columns of draggable
 * cards would be two columns of nothing.
 *
 * Dragging a definition onto a term is offered on top of that, for a pointer
 * on a wide screen. It ends in the same `assign` the dropdown calls, so the
 * two cannot mean different things. It is a pointer enhancement only: the
 * definition cards are not in the tab order and do not describe themselves
 * as draggable, because there is no keyboard drag here and the dropdowns
 * already are the whole keyboard path. A card that took focus would be a stop
 * that does nothing.
 *
 * **Nothing here knows the pairing.** The two columns arrive as separate lists
 * whose ids share no value, the repository behind them never loads a whole
 * pair row, and the student-facing GraphQL type has no field for it. This is
 * the third lock rather than the first: there is no `correct` prop and nothing
 * for one to render.
 */
export function MatchingBoard({ legend, leftItems, rightItems, value, onChange, disabled = false, error, idPrefix, className, }) {
    const t = useTranslate();
    const generatedId = useId();
    const prefix = idPrefix ?? generatedId;
    const [dragging, setDragging] = useState(null);
    const [announcement, setAnnouncement] = useState('');
    const rightById = new Map(rightItems.map((item) => [item.id, item]));
    const free = unmatched(rightItems, value);
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
    function textOf(rightId) {
        return rightId === undefined ? '' : (rightById.get(rightId)?.text ?? '');
    }
    function put(leftId, rightId) {
        const left = leftItems.find((item) => item.id === leftId);
        onChange(assign(value, leftId, rightId));
        setAnnouncement(t('ui.matching.announceMatched', [textOf(rightId), left?.text ?? '']));
    }
    function take(leftId) {
        const left = leftItems.find((item) => item.id === leftId);
        onChange(clear(value, leftId));
        setAnnouncement(t('ui.matching.announceUnmatched', [left?.text ?? '']));
    }
    function onDragEnd(event) {
        setDragging(null);
        const target = event.over?.data.current;
        if (!target)
            return;
        put(target.leftId, String(event.active.id).slice(prefix.length + 1));
    }
    const board = (
    /**
     * Sized by the room *this board* has, not by the window.
     *
     * Gated on the viewport, a board inside a narrow panel on a wide screen
     * splits into two columns it cannot afford — a term clipped mid-word, a
     * select squashed to its chevron, the label printed over the text.
     *
     * ── Why this is flex-wrap and not a container query ─────────────────
     *
     * It was `@container` with `@3xl:` columns, and Chromium laid that out
     * as nothing: the board rendered at 0×0 — no terms, no definitions —
     * whenever a layout was forced in the same task that mounted it. The
     * runner does exactly that, focusing the question on every move, so a
     * student reaching a matching question with Next, or opening a practice
     * that starts with one, saw an empty question. Only resuming onto it
     * worked, because that jump happens after a paint. See the
     * `AfterQuestionNavigation` story.
     *
     * Wrapping needs no container, so there is nothing for that to break.
     * The bases are 3:2 and grow 3:2, so side by side the columns are
     * exactly the 3fr/2fr split the grid drew; together with the gap they
     * add up to 48rem, the `@3xl` the columns used to split at. Below that
     * each column takes a line of its own.
     */
    _jsxs("div", { className: "flex flex-wrap gap-6", children: [_jsxs("section", { "aria-labelledby": `${prefix}-terms`, className: "flex min-w-0 grow-3 basis-[27.9rem] flex-col gap-3", children: [_jsx("h3", { id: `${prefix}-terms`, className: "text-label font-semibold uppercase tracking-wide text-text-muted", children: t('ui.matching.termsHeading') }), _jsx("ul", { "aria-labelledby": `${prefix}-terms`, className: "flex flex-col gap-3", children: leftItems.map((left) => {
                            const matchedId = value[left.id];
                            return (_jsx(TermRow, { prefix: prefix, leftId: left.id, disabled: disabled, children: _jsxs("div", { className: "flex min-w-0 flex-1 flex-wrap items-end gap-x-4 gap-y-2 p-3", children: [_jsx("p", { className: "min-w-0 grow basis-[12.5rem] text-body font-semibold text-text text-pretty", children: _jsx(RichText, { inline: true, children: left.text }) }), _jsxs("div", { className: "flex min-w-0 grow basis-[12.5rem] items-end gap-2", children: [_jsx("div", { className: "min-w-0 flex-1", children: _jsx(Select, { label: t('ui.matching.chooseFor', [left.text]), 
                                                        // No placeholder: the value is never unset, because
                                                        // "Not matched" is a real choice the student can make
                                                        // to clear a term rather than an absence of one.
                                                        disabled: disabled, value: matchedId ?? UNMATCHED, onValueChange: (next) => next === UNMATCHED ? take(left.id) : put(left.id, next), options: [
                                                            { value: UNMATCHED, label: t('ui.matching.notMatched') },
                                                            // Whatever is already here, plus everything still
                                                            // free. A right item on another term is not offered:
                                                            // choosing it would silently unmatch that one.
                                                            ...(matchedId === undefined
                                                                ? []
                                                                : [{ value: matchedId, label: textOf(matchedId) }]),
                                                            ...free.map((item) => ({ value: item.id, label: item.text })),
                                                        ] }) }), _jsx(IconButton, { icon: _jsx(Icon.Undo2, {}), label: t('ui.matching.clear', [left.text]), disabled: disabled || matchedId === undefined, onClick: () => take(left.id), className: "mb-[2px]" })] })] }) }, left.id));
                        }) })] }), _jsxs("section", { "aria-labelledby": `${prefix}-definitions`, className: "flex min-w-0 grow-2 basis-[18.6rem] flex-col gap-3", children: [_jsx("h3", { id: `${prefix}-definitions`, className: "text-label font-semibold uppercase tracking-wide text-text-muted", children: t('ui.matching.definitionsHeading') }), _jsx("ul", { "aria-labelledby": `${prefix}-definitions`, className: "flex flex-col gap-2", children: rightItems.map((right) => {
                            const holder = leftHolding(value, right.id);
                            const onTerm = leftItems.find((item) => item.id === holder);
                            return (_jsx("li", { children: _jsx(DefinitionCard, { prefix: prefix, rightId: right.id, text: right.text, disabled: disabled, dragging: dragging === right.id, 
                                    // Said in words, not only by dimming: a definition already
                                    // in use names the term it is on.
                                    usedBy: onTerm?.text ?? null }) }, right.id));
                        }) })] })] }));
    return (_jsxs("fieldset", { className: cn('flex flex-col gap-6', className), children: [_jsx("legend", { className: "mb-6 max-w-measure-max text-question text-text text-pretty", children: _jsx(RichText, { children: legend }) }), _jsx("p", { className: "text-body-sm text-text-secondary", children: t('ui.matching.instructions') }), disabled ? (board) : (_jsx(DndContext, { sensors: sensors, collisionDetection: closestCenter, onDragStart: (event) => setDragging(String(event.active.id).slice(prefix.length + 1)), onDragEnd: onDragEnd, onDragCancel: () => setDragging(null), accessibility: SILENT_DRAG_ACCESSIBILITY, children: board })), error ? _jsx(FieldMessage, { tone: "error", children: error }) : null, _jsx(LiveRegion, { message: announcement })] }));
}
/** One term. A drop target for a definition being dragged onto it. */
function TermRow({ prefix, leftId, disabled, children, }) {
    const { setNodeRef, isOver } = useDroppable({
        id: `${prefix}-term-${leftId}`,
        data: { leftId },
        disabled,
    });
    return (_jsx("li", { ref: setNodeRef, className: cn('flex min-w-0 rounded-md border-2 bg-surface transition-colors', isOver ? 'border-primary bg-primary-light' : 'border-border'), children: children }));
}
/**
 * One definition.
 *
 * The whole card is the drag handle rather than a grip, because there is no
 * control inside it to compete with — the choosing happens on the term rows.
 *
 * Only the pointer listeners are spread, never the library's `attributes`:
 * those would make the card a `role="button"` tab stop announced as
 * "draggable", for a keyboard drag this board does not have.
 */
function DefinitionCard({ prefix, rightId, text, disabled, dragging, usedBy, }) {
    const t = useTranslate();
    const { listeners, setNodeRef, transform } = useDraggable({
        id: `${prefix}-${rightId}`,
        disabled,
    });
    return (_jsxs("div", { ref: setNodeRef, style: transform
            ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: 10 }
            : undefined, ...(disabled ? {} : listeners), className: cn('flex min-w-0 flex-col gap-1 rounded-option border-2 px-4 py-2.5 text-body', 
        // touch-none is what lets a drag start on a finger. The dropdown is
        // the interaction that does not need it.
        disabled ? 'border-border bg-surface' : 'cursor-grab touch-none', usedBy === null ? 'border-border bg-surface' : 'border-primary bg-primary-light', dragging && 'opacity-50'), children: [_jsx("span", { className: "break-words text-text text-pretty", children: _jsx(RichText, { inline: true, children: text }) }), usedBy === null ? null : (_jsx("span", { className: "text-body-sm font-semibold text-primary", children: t('ui.matching.matchedWith', [usedBy]) }))] }));
}
//# sourceMappingURL=MatchingBoard.js.map